import { THEMES } from './themes.js';

const TIME_ZONE = 'America/Vancouver';
export const THEME_OVERRIDE_KEY = 'jonnys-home-theme-override';

function getParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date);

  return Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, Number(part.value)]));
}

function utcDay(year, month, day) {
  return Date.UTC(year, month - 1, day);
}

function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return utcDay(year, month, day);
}

function canadaThanksgiving(year) {
  const first = new Date(Date.UTC(year, 9, 1));
  const daysUntilMonday = (8 - first.getUTCDay()) % 7;
  return Date.UTC(year, 9, 1 + daysUntilMonday + 7);
}

function inFixedRange(parts, startMonth, startDay, endMonth, endDay) {
  const current = parts.month * 100 + parts.day;
  const start = startMonth * 100 + startDay;
  const end = endMonth * 100 + endDay;
  return start <= end ? current >= start && current <= end : current >= start || current <= end;
}

function inRelativeRange(currentUtc, anchorUtc, fromDays, toDays) {
  const day = 86400000;
  return currentUtc >= anchorUtc + fromDays * day && currentUtc <= anchorUtc + toDays * day;
}

export function getAutomaticTheme(date = new Date()) {
  const parts = getParts(date);
  const currentUtc = utcDay(parts.year, parts.month, parts.day);
  const easter = easterSunday(parts.year);
  const thanksgiving = canadaThanksgiving(parts.year);

  const matches = [
    { id: 'easter', priority: 98, active: inRelativeRange(currentUtc, easter, -14, 1) },
    { id: 'christmas', priority: 90, active: inFixedRange(parts, 12, 15, 12, 26) },
    { id: 'new-year', priority: 86, active: inFixedRange(parts, 12, 27, 1, 5) },
    { id: 'halloween', priority: 82, active: inFixedRange(parts, 8, 25, 10, 31) },
    { id: 'remembrance', priority: 80, active: inFixedRange(parts, 11, 7, 11, 11) },
    { id: 'thanksgiving', priority: 84, active: inRelativeRange(currentUtc, thanksgiving, -7, 0) },
    { id: 'canada-day', priority: 70, active: inFixedRange(parts, 6, 24, 7, 2) },
    { id: 'st-patricks', priority: 65, active: inFixedRange(parts, 3, 1, 3, 17) },
    { id: 'valentines', priority: 60, active: inFixedRange(parts, 2, 1, 2, 14) },
  ];

  return matches.filter((item) => item.active).sort((a, b) => b.priority - a.priority)[0]?.id ?? 'default';
}

export function getPreviewTheme() {
  const params = new URLSearchParams(window.location.search);
  const requested = params.get('theme');
  return requested && THEMES[requested] ? requested : null;
}

export function getStoredOverride() {
  try {
    const stored = localStorage.getItem(THEME_OVERRIDE_KEY);
    return stored && (stored === 'automatic' || THEMES[stored]) ? stored : 'automatic';
  } catch {
    return 'automatic';
  }
}

export function setStoredOverride(themeId) {
  if (themeId !== 'automatic' && !THEMES[themeId]) throw new Error('Unknown theme');
  localStorage.setItem(THEME_OVERRIDE_KEY, themeId);
  window.dispatchEvent(new CustomEvent('jonnys-theme-change', { detail: themeId }));
}

export function resolveTheme({ override = getStoredOverride(), date = new Date(), allowPreview = false } = {}) {
  const preview = allowPreview ? getPreviewTheme() : null;
  if (preview) return { id: preview, source: 'preview', theme: THEMES[preview] };
  if (override !== 'automatic' && THEMES[override]) return { id: override, source: 'manual', theme: THEMES[override] };
  const automatic = getAutomaticTheme(date);
  return { id: automatic, source: 'automatic', theme: THEMES[automatic] };
}
